<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1000"
  >
    <template v-slot:activator="{ on:dialog }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltip }">
          <v-btn
            icon
            v-on="{ ...dialog, ...tooltip }"
          >
            <v-icon color="#F06292">
              mdi-account-multiple-plus
            </v-icon>
          </v-btn>
        </template>
        <span>Add Employee</span>
      </v-tooltip>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Add Company Employees"
      text="Add Newly Joined / Transferred Company Employees"
      class="mb-0"
    > 
      <form v-if="dialog">
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-card class="pa-4 elevation-5">
                <v-text-field
                  v-model="searchInput"
                  placeholder="Filter employee by name"
                  append-icon="search"
                  label="Search Employee"
                  hide-no-data
                  hide-selected
                />

                <v-autocomplete
                  v-model="filters.companies"
                  :items="companies"
                  item-text="name"
                  item-value="id"
                  value="id"
                  label="Companies"
                  placeholder="Filter employee by company"
                  clearable
                  hide-selected
                  multiple
                  deletable-chips
                  chips
                />
                
                <v-autocomplete
                  v-model="filters.positions"
                  :items="positions"
                  item-text="name"
                  item-value="id"
                  value="id"
                  label="Position"
                  placeholder="Filter employee by position"
                  clearable
                  hide-selected
                  multiple
                  deletable-chips
                  chips
                />
     
                <v-data-table
                  v-model="selectedEmployees"
                  v-validate="'required'"
                  dense
                  :loading="tableLoading"
                  :headers="headers"
                  :items="filteredEmployees"
                  item-key="company_employee_uid"
                  hide-default-footer
                  fixed-header
                  disable-pagination
                  item-value="company_employee_uid"
                  show-select
                  class="elevation-1 mb-3"
                  height="272px"
                  :error-messages="errors.collect('selectedEmployees')"
                  data-vv-name="selectedEmployees"
                  data-vv-as="employee selection"
                >
                  <template v-slot:top>
                    <v-toolbar
                      flat
                      color="white"
                    >
                      <v-toolbar-title>Employee Selection:</v-toolbar-title>
                    </v-toolbar>
                  </template>
                </v-data-table>
                <div :class="errors.first('selectedEmployees') ? 'py-0' : 'py-3'">
                  <span class="red--text text--lighten-1 caption">{{ errors.first('selectedEmployees') }}</span>
                </div>

                <v-card-actions style="right: 0px;">
                  <v-spacer />
                  <v-btn
                    :disabled="progressing"
                    text
                    @click="dialog = false"
                  >
                    Close
                  </v-btn>
                  <v-btn
                    :loading="progressing"
                    :disabled="employees.length < 1"
                    color="hrissecondary"
                    class="white--text px-10"
                    @click="submitForm"
                  >
                    Add and Compute
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </form>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      payrollPeriod: {
        type: Object,
        required: true,
      },
      payrollCycleTransaction: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        loading: false,
        progressing: false,
        companies: [],
        positions: [],
        employees: [],
        tableLoading: false,
        searchInput: '',
        filters: {
          positions: '',
          companies: '',
        },
        headers: [
          {
            text: 'Name',
            align: 'left',
            sortable: true,
            value: 'employee.person.name',
          },
          {
            text: 'Company',
            align: 'left',
            sortable: true,
            value: 'company.name',
          },
          {
            text: 'Position',
            align: 'left',
            sortable: true,
            value: 'employee.current_position.position.name',
          },
        ],
        selectedEmployees: [],
      }
    },
    computed: {
      filteredEmployees () {
        let _this = this
        _this.searchInput = _this.searchInput.trim()
        let primaryFilterEmployees = this.employees.filter(function (employee) {
          return ((employee.employee.person || {}).name || '').toLowerCase().indexOf(_this.searchInput.toLowerCase()) !== -1
        })
        let filteredPositions = this.customFilterPositions(primaryFilterEmployees)
        let filteredCompanies = this.customFilterCompanies(filteredPositions)
        
        return filteredCompanies
      },
    },

    watch: {
      dialog: function (value) {
        if (!value) return this.$resetData()

        this.tableLoading = true

        Promise.all([
          this.$api.payrollCycleTransactionCompanies().show(this.payrollCycleTransaction.id),
          this.$api.positions().list(),
          this.$api.payrollCycleTransactions().listUnsyncCompanyEmployees(this.payrollCycleTransaction.id),
        ]).then(([company, position, employee]) => {
          this.employees = _.orderBy(employee.data, ['employee.person.name', 'company.name'], ['asc', 'asc'])
          this.positions = _.orderBy(position.data, ['name'], ['asc'])
          this.companies = _.orderBy(company.data, ['name'], ['asc'])

          this.tableLoading = false
        })
      },
    },
    
    methods: {
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.setProgressing(true)
            this.$api.payrollCycleTransactions().syncAndCompute(this.payrollCycleTransaction.id, this.prepareFormData())
              .then(res => {
                this.$emit('created')
                this.$snack({ message: res.data.status })

                this.setProgressing(false)
                this.dialog = false
              })
              .catch((err) => {
                if (err.response.status === 422) {              
                  this.$snack({ 
                    color: 'error',
                    message: err.response.data.status, 
                  })
                }            
            
                this.setProgressing(false)
              })
          })
      },
      prepareFormData () {
        return {
          company_employees: this.selectedEmployees.map(emp => emp.company_employee_uid),
          status: this.payrollCycleTransaction.status,
        }
      },
      customFilterPositions (transactions) {
        if (this.filters.positions.length === 0) return transactions

        return transactions.filter(transaction => {
          let positionId = (transaction.employee.current_position || {}).position_id

          return _.intersection([positionId], this.filters.positions).length > 0
        })
      },
      customFilterCompanies (transactions) {
        if (this.filters.companies.length === 0) return transactions

        return transactions.filter(transaction => {
          let companyUid = transaction.company_uid

          return _.intersection([companyUid], this.filters.companies).length > 0
        })
      },
      setProgressing (status) {
        this.progressing = status
      },
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:5px
}
</style>
